import { useState, useEffect } from 'react';
import PageTemplate from "../Templates/PageTemplate";
import InstallModal from './Apps/InstallModal';
import ConfirmationModal from '../Modals/ConfirmationModal';
import './AppStore.css';
import { BorderedSearchBar } from '../HouseStyle/Components/SearchBars';
import middleware from '../Api/Middleware';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStore, faSearch, faRepeat } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useParams } from 'react-router';
import ToastError from '../Toasts/ToastError';
import ToastPromise from '../Toasts/ToastPromise';

/**
 * AppStore Component - Main application marketplace interface
 * 
 * OVERVIEW:
 * This component serves as a marketplace where users can browse, install, and manage applications.
 * It provides a grid-based layout with categorization, search functionality, and detailed app management.
 * 
 * HOW APP LOADING WORKS:
 * 1. Apps are loaded from the backend API via the `/appstore` endpoint
 * 2. Each app has metadata including: name, slug, status, categories, logo, background_color
 * 3. App components are dynamically loaded using React's dynamic imports when selected
 * 4. The system converts app slugs (e.g., "woo-commerce") to PascalCase component names ("WooCommerce")
 * 5. Components are expected to exist at: `./Apps/{ComponentName}/{ComponentName}.js`
 * 
 * APP LOGO/IMAGE SYSTEM:
 * - App logos are stored in the `./AppLogos/` directory
 * - The backend provides a logo filename in the app metadata
 * - safeRequireLogo() handles the dynamic loading using require() for each logo file
 * - If a logo file is missing or the filename is invalid, the system gracefully falls back to no logo
 * - Logos are processed during app data loading and cached along with other app metadata
 * - The system validates logo filenames and handles edge cases (null, 'null', empty strings)
 * 
 * APP STATUS SYSTEM:
 * - Status 1: Not installed (shows install modal)
 * - Status 2: Installed but not active (can be opened) - Useful if you need extra information before the app can be used
 * - Status 3: Active/Connected (can be opened, shows as "Actief")
 * 
 * CACHING STRATEGY:
 * - App data is cached in localStorage under the 'appstore' key
 * - Cache includes both apps and categories for faster initial load
 * - Cache is updated after installation/uninstallation operations
 * 
 * COMPONENT ARCHITECTURE:
 * - Uses a two-state layout: grid view (app browsing) vs single app view
 * - Selected apps are rendered as full-screen components within the AppStore
 * - Each app component receives standardized props: app, setSelectedApp, uninstall, etc.
 * 
 * NAVIGATION FLOW:
 * 1. /appstore - Shows main grid with all apps
 * 2. /appstore/{app-slug} - Shows specific app component if installed (status > 1)
 * 3. Apps with status 1 trigger installation modal instead of direct navigation
 */
const AppStore = () => {
    const { app_slug } = useParams(); // Extract app slug from URL
    const navigate = useNavigate();

    // Load cached data to avoid unnecessary API calls on initial load
    const cachedAppStore = JSON.parse(localStorage.getItem('appstore'));
    const [loadingPage, setLoadingPage] = useState(() => {
        return cachedAppStore?.apps ? false : true;
    });
    const [apps, setApps] = useState(() => {
        return cachedAppStore?.apps || [];
    });
    const [categories, setCategories] = useState(() => {
        return cachedAppStore?.categories || [];
    });

    // App selection and component loading
    const [selectedApp, setSelectedApp] = useState(null);
    const [AppComponent, setAppComponent] = useState(null);

    // Filtering states
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Installation modal states
    const [showInstallModal, setShowInstallModal] = useState(false);
    const [appToInstall, setAppToInstall] = useState(null);
    const [appInstalling, setAppInstalling] = useState(false);
    const [appUninstalling, setAppUninstalling] = useState(false);
    const [showUninstallConfirmation, setShowUninstallConfirmation] = useState(false);
    const [appToUninstall, setAppToUninstall] = useState(null);

    /**
     * Convert slug to PascalCase component name
     * E.g., "mollie" -> "Mollie", "woo-commerce" -> "WooCommerce"
     * 
     * @param {string} slug - The app slug from API
     * @returns {string} - The PascalCase component name
     */
    const getComponentNameFromSlug = (slug) => {
        return slug
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join('');
    };

    /**
     * Dynamic App Component Loader
     * 
     * This function handles the dynamic loading of app components:
     * 1. Attempts to import the component from the expected file path
     * 2. Sets the component in state for rendering
     * 3. Handles errors gracefully with user-friendly error messages
     * 
     * @param {Object} app - The app object containing slug and name
     */
    const loadAppComponent = async (app) => {
        try {
            const componentName = getComponentNameFromSlug(app.slug);

            // Dynamic import from AppPages folder         
            const module = await import(`./Apps/${componentName}/${componentName}`);
            setAppComponent(() => module.default);
        } catch (error) {
            console.error(`Failed to load component for ${app.slug}:`, error);
            ToastError(`Er is iets fout gegaan tijdens het inladen van de "${app.name}" app`, 'error_loading_app');
        }
    };

    useEffect(() => {
        if (app_slug && cachedAppStore?.apps) {
            const selectedApp = cachedAppStore?.apps.find(app => app.slug === app_slug);
            if (selectedApp && selectedApp.status > 1) {
                setSelectedApp(selectedApp);
                loadAppComponent(selectedApp);
            }
        } else {
            setSelectedApp(null);
            setAppComponent(null);
        }

        const loadData = async () => {
            let selectedApp = null;
            try {
                // Load apps from API
                const appstoreResponse = await middleware.get('appstore');
                const apps = appstoreResponse.data.data.apps;
                const categories = appstoreResponse.data.data.categories;
                const processedApps = apps.map(app => {
                    return {
                        ...app,
                        logo: safeRequireLogo(app.logo, app.name, app.slug),
                        categories: app.categories || []
                    }
                }).sort((a, b) => a.name.localeCompare(b.name));

                selectedApp = processedApps.find(app => app.slug === app_slug);

                if (selectedApp) {
                    // Check if this app needs installation
                    if (selectedApp.status === 1) {
                        setAppToInstall(selectedApp);
                        setShowInstallModal(true);
                    } else {
                        setSelectedApp(selectedApp);
                        await loadAppComponent(selectedApp);
                    }
                }

                setCategories(categories);
                setApps(processedApps);
                localStorage.setItem('appstore', JSON.stringify({
                    apps: processedApps,
                    categories: categories
                }));
            } catch (error) {
                console.error('Error loading app store data:', error);
            } finally {
                setLoadingPage(false);
            }
        };

        loadData();
    }, [app_slug]);

    const safeRequireLogo = (logoFileName, appName, appSlug) => {
        // If no filename is provided default to slug-based name
        if (!logoFileName) {
            logoFileName = getComponentNameFromSlug(appSlug) + '.png';
        };

        // Check if we have a logo filename from API
        if (logoFileName && logoFileName !== null && logoFileName !== 'null' && logoFileName.trim() !== '') {
            try {
                return require(`./AppLogos/${logoFileName}`);
            } catch (error) {
                console.warn(`Could not load logo file ${logoFileName} for ${appName}:`, error);
            }
        }
        return null;
    };

    // Filter apps based on selected category and search term
    const filteredApps = apps.filter(app => {
        // Check if app matches selected category
        const matchesCategory = selectedCategory === 'all' || 
            (app.categories && app.categories.some(category => category.slug === selectedCategory));
        
        // Check if app matches search term
        const matchesSearch = app.name.toLowerCase().includes(searchTerm.toLowerCase());
        
        return matchesCategory && matchesSearch;
    });

    const handleCategoryClick = (categoryId) => {
        setSelectedCategory(categoryId);
    };

    const handleAppAction = (app) => {
        if (app.status === 1) {
            // Show install modal for apps with status 1
            setAppToInstall(app);
            setShowInstallModal(true);
        } else {
            // Navigate to app page for other statuses
            navigate(`/appstore/${app.slug}`);
        }
    };

    const handleInstallApp = async (app) => {
        // Create a promise that ensures minimum 1 second duration
        const installWithExtraWait = async () => {
            // Make the API call
            const response = await middleware.post(`appstore/install/${app.slug}`);
            
            // Wait a bid before the promise is resolved.
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            return response;
        };

        setAppInstalling(true);

        try {
            const response = await ToastPromise({
                promise: installWithExtraWait(),
                pending: `${app.name} wordt geïnstalleerd...`,
                successRenderer: (data) => `${app.name} is succesvol geïnstalleerd!`,
                errorRenderer: (error) => `Installatie van ${app.name} is mislukt`
            });

            const updatedApp = response.data.data.app;

            if (updatedApp) {
                // Update the app status in the local state
                const updatedApps = apps.map(a => 
                    a.app_id === app.app_id ? updatedApp : a
                );
                setApps(updatedApps);

                // Update localStorage cache with new app status
                const cachedData = JSON.parse(localStorage.getItem('appstore')) || {};
                localStorage.setItem('appstore', JSON.stringify({
                    ...cachedData,
                    apps: updatedApps
                }));
                
                // Close modal
                setShowInstallModal(false);
                setAppToInstall(null);

                if (app_slug) {
                    setSelectedApp(updatedApp);
                    loadAppComponent(updatedApp);
                } else {
                    // Navigate to the app
                    navigate(`/appstore/${app.slug}`);
                }
            }
        } catch (error) {
            console.error('Error installing app:', error);
        } finally {
            setAppInstalling(false);
        }
    };

    const handleUninstallRequest = (event, app) => {
        event.preventDefault();
        setAppToUninstall(app);
        setShowUninstallConfirmation(true);
    };

    const uninstallApp = async () => {
        if (!appToUninstall) {
            return;
        }

        const uninstallWithExtraWait = async () => {
            await new Promise(resolve => setTimeout(resolve, 1500));
            const response = await middleware.post(`appstore/uninstall/${appToUninstall.slug}`);
            return response;
        };

        setAppUninstalling(true);

        try {
            const response = await ToastPromise({
                promise: uninstallWithExtraWait(),
                pending: `Bezig met verwijderen van ${appToUninstall.name}...`,
                successRenderer: (data) => `${appToUninstall.name} succesvol verwijderd`,
                errorRenderer: (error) => `Verwijderen van ${appToUninstall.name} mislukt`
            });

            if (response) {
                navigate('/appstore');
            }
        } catch (error) {
            console.error('Error uninstalling app:', error);
        } finally {
            setAppUninstalling(false);
            setAppToUninstall(null);
        }
    };

    const handleCloseModal = () => {
        setShowInstallModal(false);
        setAppToInstall(null);
        
        // If we were trying to access this app via URL, go back to app store
        if (app_slug && appToInstall && app_slug === appToInstall.slug) {
            navigate('/appstore');
        }
    };

    return (
        <PageTemplate navbarTitle={"App Store"} pageIsLoading={loadingPage}>
            <div className="w-100">
                {!app_slug ? (
                    <div className="main-content">
                        {/* Categories Sidebar */}
                        <div className="categories-sidebar">
                            <div className="search-containerm mb-3">
                                <BorderedSearchBar
                                    inputValue={searchTerm}
                                    setInputValue={setSearchTerm}
                                />
                            </div>
                            <div className='categories-box'>
                                <h3 className="categories-title">Categorieën</h3>
                                <div className="categories-list">
                                    {categories.map((category) => (
                                        <div
                                            key={category.id}
                                            className={`category-item ${category.slug === selectedCategory ? 'active' : ''}`}
                                            onClick={() => handleCategoryClick(category.id)}
                                        >
                                            {category.slug === selectedCategory && <div className="category-indicator"></div>}
                                            <span className="category-name">{category.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Apps Grid */}
                        <div className="apps-grid">
                            {filteredApps.length > 0 ? (
                                filteredApps.map((app) => (
                                    <div
                                        key={app.app_id}
                                        onClick={() => handleAppAction(app)}
                                        className="app-card hover"
                                        style={app.background_color ? { backgroundColor: app.background_color } : {}}
                                    >
                                        <div className="app-content">
                                            <div className="app-logo">
                                                {app.logo && <img src={app.logo} alt={app.name} />}
                                            </div>
                                        </div>
                                        
                                        <div className="app-footer">
                                            <div className="app-info">
                                                <h4 className="app-name">{app.name}</h4>
                                                <div className='d-flex justify-content-between'>
                                                    <div className="app-status">
                                                        <span className="app-status-icon">
                                                            {'🔗'}
                                                        </span>
                                                        <span className="app-status-text">
                                                            {app.status === 3 ? 'Actief' : app.status === 2 ? 'Geïnstalleerd' : 'Niet actief'}
                                                        </span>
                                                    </div>
                                                    {/* Show categories as badges */}
                                                    {app.categories && app.categories.length > 0 && (
                                                        <div className="app-categories">
                                                            {app.categories.slice(0, 2).map((category) => (
                                                                <span key={category.id} className="category-badge">
                                                                    {category.name}
                                                                </span>
                                                            ))}
                                                            {app.categories.length > 2 && (
                                                                <span className="category-badge-more">
                                                                    +{app.categories.length - 2}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="empty-state">
                                    <div className="empty-state-icon">
                                        <FontAwesomeIcon icon={searchTerm ? faSearch : faStore} />
                                    </div>
                                    <h3 className="empty-state-title">Geen apps gevonden</h3>
                                    <p className="empty-state-description">
                                        {searchTerm ? 
                                            `Geen apps gevonden voor "${searchTerm}". Probeer een andere zoekopdracht.` :
                                            selectedCategory === 'all' ? 
                                                'Er zijn momenteel geen apps beschikbaar.' :
                                                `Geen apps gevonden in de categorie "${categories.find(cat => cat.id === selectedCategory)?.name}". Probeer een andere categorie.`
                                        }
                                    </p>
                                    {(searchTerm || selectedCategory !== 'all') && (
                                        <button 
                                            className="empty-state-button"
                                            onClick={() => {
                                                setSearchTerm('');
                                                handleCategoryClick('all');
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faRepeat} />
                                            Toon alle apps
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    selectedApp && AppComponent && (
                        <AppComponent
                            app={selectedApp}
                            setSelectedApp={setSelectedApp}
                            setLoadingPage={setLoadingPage}
                            uninstall={handleUninstallRequest}
                            uninstalling={appUninstalling}
                        />
                    )
                )}
            </div>
            
            {/* Install Modal */}
            <InstallModal
                showModal={showInstallModal}
                app={appToInstall}
                onClose={handleCloseModal}
                onInstall={handleInstallApp}
                installing={appInstalling}
            />

            {/* confirmation modal */}
            <ConfirmationModal
                showConfirmation={showUninstallConfirmation}
                setShowConfirmation={setShowUninstallConfirmation}
                acceptationFunction={uninstallApp}
                message={appToUninstall ? (
                    `Weet je zeker dat je ${appToUninstall.name} wilt ${appToUninstall.status === 3 ? 'ontkoppelen' : 'verwijderen'}? ` +
                    `${appToUninstall.status === 3 ? 'Alle actieve verbindingen worden verbroken en' : 'Alle'} instellingen worden gewist. ` +
                    'Je kunt de app altijd later nog opnieuw installeren.'
                ) : 'Weet je zeker dat je deze actie wilt uitvoeren?'}
                cancelButtonText="Annuleren"
                acceptButtonText="Bevestigen"
            />
        </PageTemplate>
    );
}

export default AppStore;